import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Snackbar,
    Alert,
    Button,
    LinearProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCamera,
    faFileImport,
    faFileInvoice,
    faStickyNote,
    faCalendar,
    faBriefcase,
    faLandmark,
    faGlobe,
    faChevronRight,
    faCheckCircle,
    faCloudArrowUp,
    faMagnifyingGlass,
    faBrain,
} from '@fortawesome/free-solid-svg-icons';
import { documentsApi } from '../api/documents';
import { supabase } from '../lib/supabaseClient';
import DocumentsHistory from '../components/DocumentsHistory';
import AiMonthlyCreditsSummary from '../components/AiMonthlyCreditsSummary';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import {
    validateFile,
    getApiErrorMessage,
    humanizeError,
    CONTEXT_DOMAIN_HINTS,
} from '../lib/documentValidation';

// Context Options for Picker
const CONTEXT_OPTIONS = [
    { id: 'resume', label: 'Resume / CV', icon: faBriefcase, desc: 'Extracts skills, history, and applies for you.' },
    { id: 'bill', label: 'Bill / Invoice', icon: faFileInvoice, desc: 'Extracts amount, due date, and vendor.' },
    { id: 'official', label: 'Official / Government / Legal', icon: faLandmark, desc: 'IRS, court, DMV, and other official notices.' },
    { id: 'note', label: 'Note / Memo', icon: faStickyNote, desc: 'Extracts action items and summaries.' },
    { id: 'event', label: 'Event Flyer', icon: faCalendar, desc: 'Extracts date, location, and topic.' },
    { id: 'general', label: 'General / Other', icon: faGlobe, desc: 'Generic analysis for anything else.' },
];

type UploadStep = 'idle' | 'uploading' | 'reading' | 'analyzing' | 'stored' | 'done' | 'done_no_task' | 'error';

const STEP_LABELS: Record<UploadStep, string> = {
    idle: '',
    uploading: 'Uploading your file...',
    reading: 'Reading the document...',
    analyzing: 'Understanding what this means for you...',
    stored: 'Upload complete. Analysis continues below.',
    done: 'All done! Your task is ready.',
    done_no_task: 'All done! Your file is safely stored.',
    error: 'Something went wrong',
};

const STEP_ICONS: Record<UploadStep, any> = {
    idle: null,
    uploading: faCloudArrowUp,
    reading: faMagnifyingGlass,
    analyzing: faBrain,
    stored: faCheckCircle,
    done: faCheckCircle,
    done_no_task: faCheckCircle,
    error: null,
};

const INTAKE_SUBTITLES: Record<string, string | null> = {
    job_hunt: 'Resume & Job Intake',
    finance: 'Bills & Statements',
    parenthood: 'Forms & Schedules',
    business: 'Contracts & Invoices',
    personal: 'Personal Admin',
    health: 'Care & Insurance',
    general: null,
};

const DocumentsPage: React.FC = () => {
    const { bootstrap, loading: bootstrapLoading, error: bootstrapError, refresh: refreshBootstrap } = useAppBootstrap();
    const navigate = useNavigate();
    // State
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [contextOpen, setContextOpen] = useState(false);
    const [uploadStep, setUploadStep] = useState<UploadStep>('idle');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [pendingContextId, setPendingContextId] = useState<string | null>(null);
    const [creditConfirmOpen, setCreditConfirmOpen] = useState(false);
    const [creditsPaywall, setCreditsPaywall] = useState<{
        remaining: number;
        resetDate?: string | null;
        message?: string;
    } | null>(null);

    // Refs for hidden inputs
    const photoInputRef = useRef<HTMLInputElement>(null);
    const docInputRef = useRef<HTMLInputElement>(null);

    const uploading = uploadStep !== 'idle' && uploadStep !== 'error';

    // Handlers
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            e.target.value = '';
            return;
        }

        // Client-side validation using shared module
        const validation = validateFile(file);
        if (!validation.valid) {
            setToast({ msg: validation.friendlyError || validation.error || 'Invalid file', type: 'error' });
            e.target.value = '';
            return;
        }

        setSelectedFile(file);
        setContextOpen(true);
        e.target.value = '';
    };

    const runDocumentIngest = async (contextId: string) => {
        if (!selectedFile) return;
        if (!supabase) {
            setToast({ msg: 'Storage is not configured. Please contact support.', type: 'error' });
            setSelectedFile(null);
            return;
        }

        setUploadStep('uploading');
        let documentId: string | null = null;
        let uploadSucceeded = false;
        let settledStep: UploadStep | null = null;

        try {
            const initRes = await documentsApi.initDocument({
                filename: selectedFile.name,
                mime_type: selectedFile.type,
                size_bytes: selectedFile.size,
            });
            documentId = initRes.document_id;

            setUploadStep('reading');
            const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(initRes.storage_path, selectedFile, {
                    contentType: selectedFile.type,
                    upsert: false,
                });

            if (uploadError) throw uploadError;
            uploadSucceeded = true;

            setUploadStep('analyzing');
            const hintDomain = CONTEXT_DOMAIN_HINTS[contextId];
            const processRes = await documentsApi.processDocument(initRes.document_id, { intent: contextId, hint_domain: hintDomain });

            if ((processRes as any).async) {
                settledStep = 'stored';
                setUploadStep('stored');
                await new Promise(resolve => setTimeout(resolve, 800));
                setToast({
                    msg: 'Your document is safely stored. Analysis is still in progress—a task will appear only if extraction produces one.',
                    type: 'info',
                });
            } else {
                const primaryId = processRes.work_item?.id || processRes.work_items?.[0]?.id;
                const step = primaryId ? 'done' : 'done_no_task';
                settledStep = step;
                setUploadStep(step);
                await new Promise(resolve => setTimeout(resolve, 1200));
                setToast({
                    msg: primaryId
                        ? 'Your document is safely stored and a task has been created.'
                        : 'Your document is safely stored. No automatic task was added—you can create one from Documents if you want (optional).',
                    type: 'success',
                });
            }
            setRefreshTrigger((prev) => prev + 1);
            // AC3: refresh only when this ingest may have consumed a ledger credit (image/* sync path today).
            // Async 202: finalize later; DocumentsHistory calls onEntitlementsRefresh for terminal image docs.
            const isImageIngest = (selectedFile.type || '').toLowerCase().startsWith('image/');
            if (!(processRes as any).async && isImageIngest) {
                void refreshBootstrap();
            }
        } catch (error: any) {
            console.error(error);
            settledStep = 'error';
            setUploadStep('error');
            const apiDetail = error?.response?.data?.detail;
            const creditExhausted = apiDetail && typeof apiDetail === 'object' && apiDetail.code === 'AI_CREDITS_EXHAUSTED';
            const detail = getApiErrorMessage(error);
            const message = humanizeError(detail);

            if (documentId && !uploadSucceeded) {
                try {
                    const hintDomain = CONTEXT_DOMAIN_HINTS[contextId];
                    await documentsApi.processDocument(documentId, { intent: contextId, hint_domain: hintDomain });
                } catch (processError) {
                    console.warn('Failed to mark document as failed:', processError);
                } finally {
                    setRefreshTrigger((prev) => prev + 1);
                }
            } else if (uploadSucceeded) {
                setRefreshTrigger((prev) => prev + 1);
            }

            if (creditExhausted) {
                setCreditsPaywall({
                    remaining: Number(apiDetail.remaining ?? 0),
                    resetDate: apiDetail.reset_date || bootstrap?.entitlements?.reset_date || bootstrap?.user?.entitlements?.reset_date || null,
                    message: apiDetail.message || 'AI credits are exhausted for this cycle.',
                });
                setToast({
                    msg: 'Upload completed. AI extraction is paused until credits reset.',
                    type: 'info',
                });
            } else {
                let errorMsg: string;
                if (uploadSucceeded) {
                    errorMsg = `Your file is safe, but we couldn't analyze it yet. ${message}`;
                } else if (documentId) {
                    errorMsg = `Upload may have failed. ${message}`;
                } else {
                    errorMsg = message || 'Something went wrong. Please try again.';
                }
                setToast({ msg: errorMsg, type: 'error' });
            }

            // After upload, server may reserve/finalize ledger for image/* even when processDocument throws.
            const isImageIngest = (selectedFile.type || '').toLowerCase().startsWith('image/');
            if (uploadSucceeded && isImageIngest) {
                void refreshBootstrap();
            }
        } finally {
            setTimeout(() => {
                setUploadStep('idle');
                setSelectedFile(null);
                setPendingContextId(null);
            }, settledStep === 'done' || settledStep === 'stored' ? 0 : 500);
        }
    };

    const handleContextSelect = async (contextId: string) => {
        setContextOpen(false);
        if (!selectedFile) return;
        const isImageUpload = (selectedFile.type || '').toLowerCase().startsWith('image/');
        if (isImageUpload) {
            setPendingContextId(contextId);
            setCreditConfirmOpen(true);
            return;
        }
        await runDocumentIngest(contextId);
    };

    const handleConfirmCreditUse = async () => {
        if (!pendingContextId) return;
        setCreditConfirmOpen(false);
        await runDocumentIngest(pendingContextId);
    };

    const handleCancelCreditUse = () => {
        setCreditConfirmOpen(false);
        setPendingContextId(null);
        setSelectedFile(null);
    };

    const progressValue = uploadStep === 'uploading' ? 25
        : uploadStep === 'reading' ? 50
            : uploadStep === 'analyzing' ? 75
                : uploadStep === 'stored' || uploadStep === 'done' || uploadStep === 'done_no_task' ? 100
                    : 0;

    const activeDomainKey = (bootstrap?.active_domain_key || bootstrap?.active_domain_config?.key || 'general').toString().trim().toLowerCase();
    const intakeSubtitle = useMemo(() => INTAKE_SUBTITLES[activeDomainKey] ?? null, [activeDomainKey]);

    const entitlementsSnapshot = bootstrap?.entitlements ?? bootstrap?.user?.entitlements;
    const bootstrapBlockingLoad = bootstrapLoading && !bootstrap;

    return (
        <Box sx={{
            minHeight: '100vh',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            py: 4,
            px: { xs: 2, sm: 3, md: 4 },
        }}>
            <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
                {/* INTAKE HEADER */}
                <Box textAlign="center" mb={6}>
                    <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1, color: 'var(--color-text-primary)' }}
                    >
                        Intake
                    </Typography>
                    {intakeSubtitle && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--color-text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em' }}>
                            {intakeSubtitle}
                        </Typography>
                    )}
                    <Typography variant="body1" sx={{ color: 'var(--color-text-secondary)' }}>
                        Digitize your life. Upload anything, we'll sort it out.
                    </Typography>
                    <Box
                        sx={{
                            mt: 3,
                            py: 2,
                            px: 2,
                            borderRadius: 2,
                            border: '1px solid var(--color-border)',
                            bgcolor: 'var(--color-bg-card)',
                            maxWidth: 440,
                            mx: 'auto',
                        }}
                    >
                        <AiMonthlyCreditsSummary
                            variant="intake_strip"
                            bootstrapBlockingLoad={bootstrapBlockingLoad}
                            bootstrapError={bootstrapError}
                            entitlements={entitlementsSnapshot}
                        />
                    </Box>
                </Box>

                {/* ACTION CARDS */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3, mb: 6 }}>
                    {/* 1. Snap / Photo */}
                    <Card
                        variant="outlined"
                        onClick={() => photoInputRef.current?.click()}
                        sx={{
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            cursor: 'pointer',
                            borderColor: 'var(--color-border)',
                            bgcolor: 'var(--color-bg-card)',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                borderColor: 'var(--color-accent)',
                                boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                            },
                        }}
                    >
                        <Box sx={{ p: 2, borderRadius: '50%', bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontSize: '2rem' }}>
                            <FontAwesomeIcon icon={faCamera} />
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="h6" fontWeight={700}>
                                Snap Photo
                            </Typography>
                            <Typography variant="body2" color="var(--color-text-secondary)">
                                Resume, Mail, Receipt, Flyer
                            </Typography>
                        </Box>
                        <input type="file" accept="image/jpeg,image/png,application/pdf" hidden ref={photoInputRef} onChange={handleFileSelect} />
                    </Card>

                    {/* 2. Import File */}
                    <Card
                        variant="outlined"
                        onClick={() => docInputRef.current?.click()}
                        sx={{
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            cursor: 'pointer',
                            borderColor: 'var(--color-border)',
                            bgcolor: 'var(--color-bg-card)',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                borderColor: 'var(--color-accent)',
                                boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                            },
                        }}
                    >
                        <Box sx={{ p: 2, borderRadius: '50%', bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontSize: '2rem' }}>
                            <FontAwesomeIcon icon={faFileImport} />
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="h6" fontWeight={700}>
                                Import File
                            </Typography>
                            <Typography variant="body2" color="var(--color-text-secondary)">
                                PDF, Word, Text
                            </Typography>
                        </Box>
                        <input type="file" accept=".pdf,.docx,.txt" hidden ref={docInputRef} onChange={handleFileSelect} />
                    </Card>
                </Box>

                {/* CONTEXT PICKER DIALOG */}
                <Dialog open={contextOpen} onClose={() => setContextOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700 }}>What kind of document is this?</DialogTitle>
                    <DialogContent>
                        <List sx={{ pt: 0 }}>
                            {CONTEXT_OPTIONS.map((opt) => (
                                <ListItem key={opt.id} disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        onClick={() => handleContextSelect(opt.id)}
                                        sx={{
                                            borderRadius: 2,
                                            border: '1px solid var(--color-border)',
                                            '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.05)', borderColor: '#3b82f6' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40, color: 'var(--color-text-secondary)' }}>
                                            <FontAwesomeIcon icon={opt.icon} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography fontWeight={600}>{opt.label}</Typography>}
                                            secondary={opt.desc}
                                        />
                                        <FontAwesomeIcon icon={faChevronRight} style={{ color: 'var(--color-border)', fontSize: '0.8rem' }} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setContextOpen(false)} color="inherit">
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={creditConfirmOpen} onClose={handleCancelCreditUse} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700 }}>Use 1 monthly AI credit?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                            This photo upload uses 1 credit from your monthly AI balance—the same numbers as Billing.
                        </Typography>
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}>
                            <AiMonthlyCreditsSummary
                                variant="dialog"
                                bootstrapBlockingLoad={bootstrapBlockingLoad}
                                bootstrapError={bootstrapError}
                                entitlements={entitlementsSnapshot}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--color-text-muted)' }}>
                            One credit covers processing this image into extracted tasks when the pipeline succeeds.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreditUse} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmCreditUse} variant="contained">
                            Continue
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={!!creditsPaywall} onClose={() => setCreditsPaywall(null)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700 }}>Monthly AI credits exhausted</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                            {creditsPaywall?.message || 'You are out of AI credits for this cycle.'}
                        </Typography>
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid var(--color-border)', bgcolor: 'var(--color-bg-card)' }}>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block' }}>
                                Plan
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {(bootstrap?.user?.tier || 'FREE').toUpperCase()}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mt: 1 }}>
                                Credits Remaining
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {creditsPaywall?.remaining ?? 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mt: 1 }}>
                                Reset Date
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {creditsPaywall?.resetDate ? new Date(creditsPaywall.resetDate).toLocaleDateString() : 'Next cycle'}
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'var(--color-text-muted)' }}>
                            You can still upload images and add tasks manually right now.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreditsPaywall(null)} color="inherit">
                            Close
                        </Button>
                        <Button disabled color="inherit">
                            Buy Credit Pack (Soon)
                        </Button>
                        <Button onClick={() => navigate('/app/billing')} variant="contained">
                            Upgrade in Billing
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* PROGRESS OVERLAY - More informative than spinner */}
                <Dialog
                    open={uploading}
                    PaperProps={{
                        sx: {
                            bgcolor: 'var(--color-bg-card)',
                            borderRadius: 3,
                            minWidth: 320,
                            maxWidth: 400,
                            p: 3,
                        }
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        {/* Animated Icon */}
                        <Box sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            bgcolor: uploadStep === 'stored' || uploadStep === 'done' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            transition: 'all 0.3s',
                        }}>
                            <FontAwesomeIcon
                                icon={STEP_ICONS[uploadStep] || faCloudArrowUp}
                                style={{
                                    fontSize: '1.75rem',
                                    color: uploadStep === 'stored' || uploadStep === 'done' ? '#10b981' : 'var(--color-accent)',
                                    animation: uploadStep === 'analyzing' ? 'pulse 1.5s infinite' : undefined,
                                }}
                            />
                        </Box>

                        {/* Step Label */}
                        <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--color-text-primary)', mb: 1 }}>
                            {STEP_LABELS[uploadStep]}
                        </Typography>

                        {/* Reassuring message */}
                        <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', mb: 3 }}>
                            {uploadStep === 'done'
                                ? 'Your document is safely stored.'
                                : uploadStep === 'stored'
                                    ? 'Your file is safe. Check the document row below for live status.'
                                : 'Your file is safe. This usually takes a few seconds.'}
                        </Typography>

                        {/* Progress Bar */}
                        <LinearProgress
                            variant="determinate"
                            value={progressValue}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'rgba(99, 102, 241, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    bgcolor: uploadStep === 'stored' || uploadStep === 'done' ? '#10b981' : 'var(--color-accent)',
                                    transition: 'transform 0.5s ease-in-out',
                                },
                            }}
                        />
                    </Box>
                </Dialog>

                {/* HISTORY SECTION */}
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'var(--color-text-primary)' }}>
                        Recent Uploads
                    </Typography>
                    <DocumentsHistory
                        refreshTrigger={refreshTrigger}
                        analyticalEnabled={
                            bootstrap?.entitlements?.analytical_enabled ??
                            bootstrap?.user?.entitlements?.analytical_enabled ??
                            false
                        }
                        onEntitlementsRefresh={() => {
                            void refreshBootstrap();
                        }}
                    />
                </Box>

                {/* TOASTS */}
                <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
                    <Alert severity={toast?.type || 'info'} sx={{ width: '100%' }}>
                        {toast?.msg}
                    </Alert>
                </Snackbar>
            </Box>

            {/* Pulse animation for analyzing step */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.7; transform: scale(1.05); }
                }
            `}</style>
        </Box>
    );
};

export default DocumentsPage;
